import { Box, Typography, Skeleton } from "@mui/material";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import { useState, useEffect } from "react";
import webFormsStyles from "./WebForms.styles";
import webFormsTokens from "./WebForms.tokens";

const { table } = webFormsTokens;

/** Placeholder rows sitting on the list card's own grid while a page loads. */
export const renderWebFormSkeletons = () =>
  Array.from({ length: 5 }).map((_, index) => (
    <Box key={index} sx={webFormsStyles.row}>
      <Box sx={webFormsStyles.rowLead}>
        <Skeleton
          variant="rounded"
          width={table.iconTileSize}
          height={table.iconTileSize}
          sx={{ borderRadius: table.iconTileRadius, flexShrink: 0 }}
        />
        <Box width="100%">
          <Skeleton variant="text" width="45%" height={18} />
          <Skeleton variant="text" width="30%" height={14} />
        </Box>
      </Box>
      <Box sx={webFormsStyles.templateColumn}>
        <Skeleton
          variant="rounded"
          width={90}
          height={22}
          sx={{ borderRadius: table.chipRadius }}
        />
      </Box>
      <Box sx={webFormsStyles.createdColumn}>
        <Skeleton variant="text" width="80%" height={16} sx={{ ml: "auto" }} />
      </Box>
      <Box sx={webFormsStyles.kebabColumn}>
        <Skeleton
          variant="circular"
          width={table.kebabSize}
          height={table.kebabSize}
        />
      </Box>
    </Box>
  ));

interface WebFormDeleteDialogProps {
  open: boolean;
  formName?: string;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const WebFormDeleteDialog = ({
  open,
  formName,
  isDeleting,
  onClose,
  onConfirm,
}: WebFormDeleteDialogProps) => (
  <AppModal
    open={open}
    onClose={onClose}
    title="Delete Web Form"
    tone="destructive"
    size="sm"
    busy={isDeleting}
    primaryLabel={isDeleting ? "Deleting..." : "Delete"}
    onPrimary={onConfirm}
    primaryLoading={isDeleting}
    cancelLabel="Cancel"
    onCancel={onClose}
  >
    <Typography sx={bodyTextSx}>
      Are you sure you want to delete the web form "{formName}"?
    </Typography>
  </AppModal>
);

interface WebFormUnsavedDialogProps {
  open: boolean;
  onClose: () => void;
  onLeave: () => void;
}

export const WebFormUnsavedDialog = ({
  open,
  onClose,
  onLeave,
}: WebFormUnsavedDialogProps) => (
  <AppModal
    open={open}
    onClose={onClose}
    title="Discard Changes?"
    // Leaving throws the edit away, so the prompt reads as destructive and a
    // stray scrim click can no longer dismiss it.
    tone="destructive"
    size="sm"
    primaryLabel="Discard Changes"
    onPrimary={onLeave}
    cancelLabel="Keep Editing"
    onCancel={onClose}
  >
    <Typography sx={bodyTextSx}>
      You have unsaved changes. Are you sure you want to leave? Your progress
      will be lost.
    </Typography>
  </AppModal>
);

interface WebFormRenameDialogProps {
  open: boolean;
  initialName?: string;
  isRenaming: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => void;
}

export const WebFormRenameDialog = ({
  open,
  initialName,
  isRenaming,
  onClose,
  onConfirm,
}: WebFormRenameDialogProps) => {
  const [name, setName] = useState(initialName || "");

  useEffect(() => {
    if (open) {
      setName(initialName || "");
    }
  }, [open, initialName]);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Rename Web Form"
      size="sm"
      busy={isRenaming}
      primaryLabel={isRenaming ? "Renaming..." : "Rename"}
      onPrimary={() => onConfirm(name)}
      primaryLoading={isRenaming}
      primaryDisabled={isRenaming || !name.trim() || name === initialName}
      cancelLabel="Cancel"
      onCancel={onClose}
    >
      <CustomInput
        label="Form Name"
        required
        value={name}
        onChange={(e: any) => setName(e.target.value)}
        placeHolder="Enter web form name"
      />
    </AppModal>
  );
};
